/**
 * Automation Runner
 * 
 * Real-time execution layer for automation rules.
 * SOURCE OF TRUTH: automationEngine.ts for rules, conditions, and execution.
 * EVENT CREATION: eventFactory.ts for all system events.
 * 
 * This module provides:
 * - Event emission with idempotency
 * - Rule evaluation against system events
 * - Action execution (notification, SMS, follow-up, admin alert)
 * - Execution logging for auditability
 * - Timeline event generation for automation activity visibility
 */

import type { AutomationRule, AutomationEvent, AutomationExecution, AutomationEventType } from "./automationEngine";
import { processAutomationEvent } from "./automationEngine";
import type { SystemEvent, SystemEventType, CommunicationEvent } from "./communicationEngine";
import type { Job } from "./types";
import { createSystemEvent } from "./eventFactory";

// ── Execution Log ──

export interface AutomationLog {
  id: string;
  ruleId: string;
  ruleName: string;
  jobId: string;
  eventType: string;
  timestamp: string;
  success: boolean;
  actionsSummary: string;
  actionType?: string;
  error?: string;
}

// ── Automation Timeline Event ──
// When automation fires, generate a CommunicationEvent for timeline visibility.

export function automationToCommunicationEvent(log: AutomationLog): CommunicationEvent {
  const isSuccess = log.success;
  return {
    id: `auto-${log.id}`,
    jobId: log.jobId,
    timestamp: log.timestamp,
    type: "system",
    subject: isSuccess
      ? `Automation: ${log.ruleName}`
      : `Automation Failed: ${log.ruleName}`,
    message: isSuccess
      ? log.actionsSummary
      : `Failed — ${log.error || log.actionsSummary}`,
    actor: "system",
    metadata: {
      source: "automation",
      ruleId: log.ruleId,
      ruleName: log.ruleName,
      actionType: log.actionType,
      success: isSuccess,
      eventType: log.eventType,
    },
  };
}

// ── Event-to-Rule Mapping ──

const SYSTEM_TO_AUTOMATION_EVENT: Partial<Record<SystemEventType, AutomationEventType>> = {
  job_created: "job_created",
  issue_created: "issue_created",
  stage_changed: "lead_stage_changed",
  scheduling_change: "appointment_scheduled",
  wrap_submitted: "wrap_submitted",
  install_completed: "install_completed",
  qa_passed: "qa_passed",
  payment_collected: "payment_collected",
  invoice_created: "job_created", // reuse for visibility
};

// ── Idempotency ──

const processedEvents = new Set<string>();
const MAX_PROCESSED_CACHE = 500;

function getEventKey(event: SystemEvent): string {
  return `${event.type}:${event.jobId}:${event.timestamp}`;
}

function markProcessed(key: string): void {
  processedEvents.add(key);
  if (processedEvents.size > MAX_PROCESSED_CACHE) {
    const first = processedEvents.values().next().value;
    if (first) processedEvents.delete(first);
  }
}

// ── Core Runner ──

export interface AutomationRunResult {
  executions: AutomationExecution[];
  logs: AutomationLog[];
  updatedRules: AutomationRule[];
  timelineEvents: CommunicationEvent[];
}

/**
 * Process a system event against all enabled automation rules.
 * Returns executions, logs, updated rules, and timeline events for visibility.
 */
export function runAutomation(
  event: SystemEvent,
  rules: AutomationRule[],
  job?: Job,
): AutomationRunResult {
  const eventKey = getEventKey(event);

  // Idempotency check
  if (processedEvents.has(eventKey)) {
    return { executions: [], logs: [], updatedRules: rules, timelineEvents: [] };
  }

  // Map system event to automation event type
  const automationEventType = SYSTEM_TO_AUTOMATION_EVENT[event.type];
  if (!automationEventType) {
    return { executions: [], logs: [], updatedRules: rules, timelineEvents: [] };
  }

  // Build AutomationEvent from SystemEvent
  const automationEvent: AutomationEvent = {
    id: event.payload?.eventId || `evt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: automationEventType,
    timestamp: event.timestamp,
    payload: {
      ...event.payload,
      jobId: event.jobId,
      customerName: job?.customerName || event.payload?.customerName || "",
      installDate: job?.scheduledInstallDate || "",
      depositAmount: job?.financials?.paymentPlan?.lineItems?.find(
        li => li.dueAt === "pre_schedule"
      )?.amount?.toLocaleString() || "",
    },
    sourceModule: automationEventType.startsWith("lead") || automationEventType === "appointment_scheduled"
      ? "pipeline"
      : automationEventType.startsWith("quote") ? "quotes"
      : automationEventType.startsWith("job") || automationEventType === "issue_created" ? "jobs"
      : "communication",
    entityId: event.jobId,
    entityName: job?.customerName || event.payload?.entityName || event.jobId,
    triggeredBy: event.payload?.triggeredBy || "System",
  };

  // Use existing processAutomationEvent from automationEngine.ts
  const executions = processAutomationEvent(automationEvent, rules);

  // Build logs
  const logs: AutomationLog[] = executions.map(exec => ({
    id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    ruleId: exec.ruleId,
    ruleName: exec.ruleName,
    jobId: event.jobId,
    eventType: event.type,
    timestamp: exec.executedAt,
    success: exec.success,
    actionsSummary: exec.actions.map(a => `${a.type}: ${a.description}`).join("; "),
    actionType: exec.actions[0]?.type,
  }));

  // Generate timeline events for each automation execution
  const timelineEvents = logs.map(automationToCommunicationEvent);

  // Update rule metadata
  const triggeredRuleIds = new Set(executions.map(e => e.ruleId));
  const updatedRules = rules.map(rule => {
    if (!triggeredRuleIds.has(rule.id)) return rule;
    return {
      ...rule,
      lastTriggeredAt: event.timestamp,
      lastTriggeredEntity: job?.customerName || event.jobId,
      triggerCount: rule.triggerCount + 1,
    };
  });

  markProcessed(eventKey);

  return { executions, logs, updatedRules, timelineEvents };
}

/**
 * Detect system events by comparing previous and current job states.
 * Uses the canonical event factory for all event creation.
 */
export function detectJobStateEvents(
  prevJob: Job,
  currentJob: Job,
): SystemEvent[] {
  const events: SystemEvent[] = [];

  // Stage change
  if (prevJob.currentStage !== currentJob.currentStage) {
    events.push(createSystemEvent({
      type: "stage_changed",
      jobId: currentJob.id,
      payload: {
        previousStage: prevJob.currentStage,
        newStage: currentJob.currentStage,
        entityName: currentJob.customerName,
      },
    }));
  }

  // Payment collected
  const prevPaidCount = prevJob.financials?.payments?.length ?? 0;
  const currPaidCount = currentJob.financials?.payments?.length ?? 0;
  if (currPaidCount > prevPaidCount) {
    events.push(createSystemEvent({
      type: "payment_collected",
      jobId: currentJob.id,
      payload: { entityName: currentJob.customerName },
    }));
  }

  // Invoice created
  const prevInvCount = prevJob.financials?.invoices?.length ?? 0;
  const currInvCount = currentJob.financials?.invoices?.length ?? 0;
  if (currInvCount > prevInvCount) {
    events.push(createSystemEvent({
      type: "invoice_created",
      jobId: currentJob.id,
      payload: { entityName: currentJob.customerName },
    }));
  }

  // QA passed
  if (prevJob.qaStatus !== "Passed" && currentJob.qaStatus === "Passed") {
    events.push(createSystemEvent({
      type: "qa_passed",
      jobId: currentJob.id,
      payload: { entityName: currentJob.customerName },
    }));
  }

  // Install completed
  if (prevJob.installStatus !== "Install Complete" && currentJob.installStatus === "Install Complete") {
    events.push(createSystemEvent({
      type: "install_completed",
      jobId: currentJob.id,
      payload: { entityName: currentJob.customerName },
    }));
  }

  // Scheduling change
  if (prevJob.scheduledInstallDate !== currentJob.scheduledInstallDate && currentJob.scheduledInstallDate) {
    events.push(createSystemEvent({
      type: "scheduled",
      jobId: currentJob.id,
      payload: {
        installDate: currentJob.scheduledInstallDate,
        entityName: currentJob.customerName,
      },
    }));
  }

  return events;
}

/**
 * Clear the idempotency cache (for testing or session reset).
 */
export function clearProcessedEvents(): void {
  processedEvents.clear();
}
